// ============================================================================
// ROLE FORM MODAL — Add / Edit Role (Phase 4)
// ============================================================================
// Pattern B internal formData. XL modal (1000px) — module/action matrix needs
// the width. The matrix is a checkbox grid: rows = modules, columns = actions.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { roleService } from 'api/services/roleService';
import type { Role } from 'api/types/role.types';
import {
  RecsuitModuleCodes,
  RecsuitActionCodes,
} from 'auth/recsuitModuleCodes';

interface RoleFormData {
  roleCode: string;
  roleName: string;
  description: string;
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
  statusCode: 'ACTIVE' | 'INACTIVE';
}

const INITIAL_FORM_DATA: RoleFormData = {
  roleCode: '',
  roleName: '',
  description: '',
  visibleModules: [],
  moduleActions: {},
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

// ============================================================================
// PERMISSION MATRIX METADATA — ported from PRIMA's RolePermissionModal
// ============================================================================

// Per-module available action verbs. CRUD verbs render as the "Actions" row;
// scope codes (view-* / manage-*) render as Self/Team/All radio rows;
// any leftover verbs render as the "Other" row.
const MODULE_AVAILABLE_ACTIONS: Record<string, string[]> = {
  // Master Data
  [RecsuitModuleCodes.Zone]:                   ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.Region]:                 ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.Area]:                   ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.Branch]:                 ['add', 'edit', 'delete', 'view', 'export'],
  // Catalogs
  [RecsuitModuleCodes.Product]:                ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.ActionType]:             ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.ClosureReason]:          ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.ProceedingType]:         ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.NoticeType]:             ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.NoticeReturnStatus]:     ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.ExpenseType]:            ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.CourtPlace]:             ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.Court]:                  ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.CourtType]:              ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.Arbitrator]:             ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.Advocate]:               ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.NoticeTemplate]:         ['add', 'edit', 'delete', 'view', 'export'],
  // Configuration
  [RecsuitModuleCodes.ConfigSettings]:         ['view', 'edit'],
  [RecsuitModuleCodes.CaseNumberFormat]:       ['add', 'edit', 'delete', 'view', 'export'],
  // Administration
  [RecsuitModuleCodes.Role]:                   ['add', 'edit', 'delete', 'view'],
  // Workflow Framework
  [RecsuitModuleCodes.WorkflowDefinitions]:    ['add', 'edit', 'delete', 'view', 'export'],
  [RecsuitModuleCodes.ApprovalInbox]:          ['view'],
  // Pre-Litigation (Phase 7) — scope-aware modules
  [RecsuitModuleCodes.PreLitigationPool]: [
    'view', 'claim', 'assign', 'manual_close', 'recompute_pool',
    'view-own', 'view-own-team', 'view-all',
    'manage-own', 'manage-own-team', 'manage-all',
  ],
  [RecsuitModuleCodes.PreLitigationCase]: [
    'view', 'log_action', 'capture_commitment', 'resolve_commitment',
    'escalate', 'assign', 'manual_close',
    'view-own', 'view-own-team', 'view-all',
    'manage-own', 'manage-own-team', 'manage-all',
  ],
  [RecsuitModuleCodes.AccountCases]: [
    'view',
    'view-own', 'view-own-team', 'view-all',
  ],
  // Litigation (Phase 9) — case-level domain actions, scope-aware.
  [RecsuitModuleCodes.LitigationCase]: [
    'view', 'edit_meta',
    'claim',
    'manage_notice', 'manage_complaint', 'manage_hearings',
    'manage_arbitration', 'manage_award',
    'manage_possession', 'manage_sarfaesi',
    'manage_close', 'escalate',
    // Phase 7 close/hold rework
    'close', 'reopen', 'hold', 'resume',
    'close-write-off', 'hold-policy-override',
    // Scope ladders
    'view-own', 'view-own-team', 'view-all',
    'manage-own', 'manage-own-team', 'manage-all',
  ],
};

// CRUD-style verbs rendered as plain checkboxes on the "Actions" row.
const CRUD_ACTION_CODES = ['add', 'edit', 'delete', 'view', 'export'] as const;

// Scope-ladder action codes — render as Self/Team/All radios.
const VIEW_SCOPE_CODES   = ['view-own', 'view-own-team', 'view-all'] as const;
const ACTION_SCOPE_CODES = ['manage-own', 'manage-own-team', 'manage-all'] as const;

// Friendly labels for non-CRUD action verbs (Pre-Lit + Litigation + future).
const OTHER_ACTION_LABELS: Record<string, string> = {
  claim:                  'Claim & Open',
  assign:                 'Assign Agent',
  log_action:             'Log Action',
  capture_commitment:     'Capture Commitment',
  resolve_commitment:     'Resolve Commitment',
  escalate:               'Escalate',
  manual_close:           'Manual Close',
  recompute_pool:         'Recompute Pool',

  // Litigation domain
  edit_meta:              'Edit Metadata',
  manage_notice:          'Manage Notices',
  manage_complaint:       'Manage Complaints',
  manage_hearings:        'Manage Hearings',
  manage_arbitration:     'Manage Arbitration',
  manage_award:           'Manage Awards',
  manage_possession:      'Manage Possession',
  manage_sarfaesi:        'Manage SARFAESI',
  manage_close:           'Manage Closure (legacy)',

  // Phase 7 case-level actions
  close:                  'Close Case',
  reopen:                 'Reopen Case',
  hold:                   'Hold Case',
  resume:                 'Resume Case',
  'close-write-off':      'Close — Allow Write-Off Reason',
  'hold-policy-override': 'Hold — Allow Policy-Override Reason',
};

type ScopeLevel = 'self' | 'team' | 'all';

const SCOPE_LEVEL_LABEL: Record<ScopeLevel, string> = {
  self: 'Self only',
  team: 'My team',
  all:  'All',
};

/** Read the current view-scope level from a module's action array. */
function readViewScope(actions: string[] | undefined): ScopeLevel {
  if (!actions) return 'self';
  if (actions.includes('view-all')) return 'all';
  if (actions.includes('view-own-team')) return 'team';
  return 'self';
}

/** Read the current manage-scope level from a module's action array. */
function readActionScope(actions: string[] | undefined): ScopeLevel {
  if (!actions) return 'self';
  if (actions.includes('manage-all')) return 'all';
  if (actions.includes('manage-own-team')) return 'team';
  return 'self';
}

/** Replace any view-scope codes in `actions` with the codes implied by `level`. */
function writeViewScope(actions: string[], level: ScopeLevel): string[] {
  const stripped = actions.filter(a => !(VIEW_SCOPE_CODES as readonly string[]).includes(a));
  if (level === 'all')  return [...stripped, 'view-all'];
  if (level === 'team') return [...stripped, 'view-own-team'];
  return [...stripped, 'view-own'];
}

/** Replace any manage-scope codes in `actions` with the codes implied by `level`. */
function writeActionScope(actions: string[], level: ScopeLevel): string[] {
  const stripped = actions.filter(a => !(ACTION_SCOPE_CODES as readonly string[]).includes(a));
  if (level === 'all')  return [...stripped, 'manage-all'];
  if (level === 'team') return [...stripped, 'manage-own-team'];
  return [...stripped, 'manage-own'];
}

// Module groups for visual organisation in the matrix.
const MODULE_GROUPS: Array<{ label: string; modules: Array<{ code: string; label: string }> }> = [
  {
    label: 'Master Data',
    modules: [
      { code: RecsuitModuleCodes.Zone, label: 'Zone' },
      { code: RecsuitModuleCodes.Region, label: 'Region' },
      { code: RecsuitModuleCodes.Area, label: 'Area' },
      { code: RecsuitModuleCodes.Branch, label: 'Branch' },
    ],
  },
  {
    label: 'Catalogs',
    modules: [
      { code: RecsuitModuleCodes.Product, label: 'Product' },
      { code: RecsuitModuleCodes.ActionType, label: 'Action Type' },
      { code: RecsuitModuleCodes.ClosureReason, label: 'Closure Reason' },
      { code: RecsuitModuleCodes.ProceedingType, label: 'Proceeding Type' },
      { code: RecsuitModuleCodes.NoticeType, label: 'Notice Type' },
      { code: RecsuitModuleCodes.NoticeReturnStatus, label: 'Notice Return Status' },
      { code: RecsuitModuleCodes.ExpenseType, label: 'Expense Type' },
      { code: RecsuitModuleCodes.CourtPlace, label: 'Court Place' },
      { code: RecsuitModuleCodes.Court, label: 'Court' },
      { code: RecsuitModuleCodes.CourtType, label: 'Court Type' },
      { code: RecsuitModuleCodes.Arbitrator, label: 'Arbitrator' },
      { code: RecsuitModuleCodes.Advocate, label: 'Advocate' },
      { code: RecsuitModuleCodes.NoticeTemplate, label: 'Notice Template' },
    ],
  },
  {
    label: 'Pre-Litigation',
    modules: [
      { code: RecsuitModuleCodes.PreLitigationPool, label: 'Pre-Litigation Pool' },
      { code: RecsuitModuleCodes.PreLitigationCase, label: 'Pre-Litigation Cases' },
      { code: RecsuitModuleCodes.AccountCases,      label: 'Account Cases' },
    ],
  },
  {
    label: 'Litigation',
    modules: [
      { code: RecsuitModuleCodes.LitigationCase, label: 'Litigation Cases' },
    ],
  },
  {
    label: 'Configuration',
    modules: [
      { code: RecsuitModuleCodes.ConfigSettings, label: 'Settings' },
      { code: RecsuitModuleCodes.CaseNumberFormat, label: 'Case Number Format' },
    ],
  },
  {
    label: 'Administration',
    modules: [
      { code: RecsuitModuleCodes.Role, label: 'Role' },
    ],
  },
  {
    label: 'Workflow Framework',
    modules: [
      { code: RecsuitModuleCodes.WorkflowDefinitions, label: 'Workflow Definitions' },
      { code: RecsuitModuleCodes.ApprovalInbox, label: 'Approval Inbox' },
    ],
  },
];

export interface RoleFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: Role | null;
  onSaved: () => void;
  onClose: () => void;

  // ----- Minimize-to-bar support -----
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<RoleFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const recordRef = useRef(record);
  recordRef.current = record;
  const initialSnapshotRef = useRef('');

  // ----- Minimize-state-capture refs -----
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback(
    (f: string) => fieldErrors[f],
    [fieldErrors],
  );
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors((prev) => {
      if (!prev[f]) return prev;
      const next = { ...prev };
      delete next[f];
      return next;
    });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const r = recordRef.current;
    const data: RoleFormData = r
      ? {
          roleCode: r.roleCode,
          roleName: r.roleName,
          description: r.description ?? '',
          visibleModules: r.visibleModules ?? [],
          moduleActions: r.moduleActions ?? {},
          statusCode: (r.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'),
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  // ----- Minimize handler -----
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.roleCode || currentFormData.roleCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.roleCode?.trim();
      const name = currentFormData.roleName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.roleCode || currentRecord?.roleCode || 'Role'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.roleCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const toggleModule = useCallback((code: string, checked: boolean) => {
    setFormData((prev) => {
      const visibleModules = checked
        ? Array.from(new Set([...prev.visibleModules, code]))
        : prev.visibleModules.filter((m) => m !== code);
      const moduleActions = { ...prev.moduleActions };
      if (!checked) {
        delete moduleActions[code];
      } else if (!moduleActions[code]) {
        // Default to view-only when first ticked.
        moduleActions[code] = [RecsuitActionCodes.View];
      }
      return { ...prev, visibleModules, moduleActions };
    });
  }, []);

  const toggleAction = useCallback((module: string, action: string, checked: boolean) => {
    setFormData((prev) => {
      const current = new Set(prev.moduleActions[module] ?? []);
      if (checked) current.add(action);
      else current.delete(action);
      const moduleActions = { ...prev.moduleActions, [module]: Array.from(current) };
      // If the user un-ticks every action and module is still visible, leave the module visible
      // with an empty action array — admins may want a "view nothing yet" placeholder row.
      return { ...prev, moduleActions };
    });
  }, []);

  /** Set a scope-ladder value (self/team/all) for a module on either ladder. */
  const setScopeLevel = useCallback((moduleId: string, ladder: 'view' | 'action', level: ScopeLevel) => {
    setFormData((prev) => {
      const current = prev.moduleActions[moduleId] ?? [];
      const updated = ladder === 'view'
        ? writeViewScope(current, level)
        : writeActionScope(current, level);
      const moduleActions = { ...prev.moduleActions, [moduleId]: updated };
      return { ...prev, moduleActions };
    });
  }, []);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.roleCode) errors.roleCode = 'Role code is required';
    else if (!/^[a-z][a-z0-9_]*$/.test(formData.roleCode)) {
      errors.roleCode = 'Lowercase letters, digits and underscore only; must start with a letter';
    }
    if (!formData.roleName) errors.roleName = 'Role name is required';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      if (mode === 'create' || mode === 'copy') {
        await roleService.create({
          roleCode: formData.roleCode.trim(),
          roleName: formData.roleName.trim(),
          description: formData.description.trim() || null,
          visibleModules: formData.visibleModules,
          moduleActions: formData.moduleActions,
        });
        notificationService.toast.success(`Role "${formData.roleCode}" created successfully.`);
      } else if (record) {
        await roleService.update(record.id, {
          // Backend requires roleCode; system roles refuse any mismatch
          // (rename is forbidden — Keycloak realm role names are pinned).
          // Send the existing code unchanged; the form does not expose
          // renaming for system roles.
          roleCode: record.roleCode,
          roleName: formData.roleName.trim(),
          description: formData.description.trim() || null,
          visibleModules: formData.visibleModules,
          moduleActions: formData.moduleActions,
          statusId: null, // server keeps existing unless an explicit ACTIVE/INACTIVE choice is wired
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Role "${record.roleCode}" updated successfully.`);
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      if (status === 409) {
        notificationService.toast.error(
          'This role was modified by another user — refresh and try again.',
          6000,
        );
      } else {
        notificationService.toast.error(
          err?.response?.data?.message || 'An error occurred. Please try again.',
          6000,
        );
      }
    }
  }, [mode, record, formData, onSaved, onClose]);

  if (!isOpen) return null;

  const isSystemRole = record?.isSystemRole ?? false;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add Role' : `Edit Role — ${record?.roleCode}`}
            {isSystemRole && (
              <span
                style={{
                  marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 600,
                  color: '#0369a1', background: '#e0f2fe', padding: '2px 8px',
                  border: '1px solid #0284c7', borderRadius: '999px',
                }}
              >
                SYSTEM
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                aria-label="Minimize"
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-grid-2">
            <div className="form-group" data-field="roleCode">
              <label className="form-label">Role Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('roleCode') ? ' form-field-error' : ''}`}
                value={formData.roleCode}
                disabled={mode === 'edit' || isSystemRole}
                onChange={(e) => {
                  clearFieldError('roleCode');
                  setFormData((p) => ({ ...p, roleCode: e.target.value.toLowerCase() }));
                }}
                placeholder="e.g. recsuit_branch_supervisor"
              />
              {getFieldError('roleCode') && (
                <div className="form-error-message">{getFieldError('roleCode')}</div>
              )}
            </div>

            <div className="form-group" data-field="roleName">
              <label className="form-label">Role Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('roleName') ? ' form-field-error' : ''}`}
                value={formData.roleName}
                onChange={(e) => {
                  clearFieldError('roleName');
                  setFormData((p) => ({ ...p, roleName: e.target.value }));
                }}
              />
              {getFieldError('roleName') && (
                <div className="form-error-message">{getFieldError('roleName')}</div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Permission Matrix</label>
            <div
              style={{
                border: '1px solid var(--form-border-color)',
                borderRadius: '8px',
                overflow: 'auto',
                maxHeight: '500px',
                padding: '0.5rem',
              }}
            >
              {MODULE_GROUPS.map((group) => (
                <div key={group.label} style={{ marginBottom: '1.25rem' }}>
                  {/* Section header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      padding: '0.4rem 0.6rem',
                      background: 'var(--bg-secondary)',
                      borderRadius: '6px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {group.label}
                    </span>
                  </div>

                  {/* Module rows */}
                  {group.modules.map((m) => {
                    const visible = formData.visibleModules.includes(m.code);
                    const acts = formData.moduleActions[m.code] ?? [];
                    const availableActions = MODULE_AVAILABLE_ACTIONS[m.code] ?? [];

                    const crudActions = availableActions.filter((a) =>
                      (CRUD_ACTION_CODES as readonly string[]).includes(a),
                    );
                    const otherActions = availableActions.filter(
                      (a) =>
                        !(CRUD_ACTION_CODES as readonly string[]).includes(a) &&
                        !(VIEW_SCOPE_CODES as readonly string[]).includes(a) &&
                        !(ACTION_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const showViewLadder = availableActions.some((a) =>
                      (VIEW_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const showActionLadder = availableActions.some((a) =>
                      (ACTION_SCOPE_CODES as readonly string[]).includes(a),
                    );
                    const currentViewLevel = readViewScope(acts);
                    const currentActionLevel = readActionScope(acts);

                    const radioRow = (
                      ladder: 'view' | 'action',
                      currentLevel: ScopeLevel,
                      labelText: string,
                    ) => (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          padding: '0.15rem 0 0.15rem 2.4rem',
                          fontSize: '0.75rem',
                          color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                        }}
                      >
                        <span style={{ minWidth: '110px', fontWeight: 500 }}>{labelText}</span>
                        {(['self', 'team', 'all'] as ScopeLevel[]).map((lvl) => (
                          <label
                            key={lvl}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                              cursor: visible ? 'pointer' : 'not-allowed',
                            }}
                          >
                            <input
                              type="radio"
                              name={`${m.code}-${ladder}`}
                              checked={currentLevel === lvl}
                              onChange={() => setScopeLevel(m.code, ladder, lvl)}
                              disabled={!visible}
                            />
                            {SCOPE_LEVEL_LABEL[lvl]}
                          </label>
                        ))}
                      </div>
                    );

                    return (
                      <div
                        key={m.code}
                        style={{
                          padding: '0.4rem 0.6rem 0.4rem 1.8rem',
                          opacity: visible ? 1 : 0.6,
                          borderBottom: '1px dashed var(--border-color)',
                        }}
                      >
                        {/* Module header — visibility toggle + module name */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            marginBottom: '0.25rem',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={visible}
                            onChange={(e) => toggleModule(m.code, e.target.checked)}
                          />
                          {m.label}
                        </label>

                        {/* CRUD action row (Add / Edit / Delete / View / Export) */}
                        {crudActions.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.15rem 0 0.15rem 2.4rem',
                              fontSize: '0.75rem',
                              color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span style={{ minWidth: '110px', fontWeight: 500 }}>Actions</span>
                            {crudActions.map((action) => (
                              <label
                                key={action}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  cursor: visible ? 'pointer' : 'not-allowed',
                                  textTransform: 'capitalize',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={acts.includes(action)}
                                  onChange={(e) => toggleAction(m.code, action, e.target.checked)}
                                  disabled={!visible}
                                />
                                {action}
                              </label>
                            ))}
                          </div>
                        )}

                        {/* View-scope ladder */}
                        {showViewLadder && radioRow('view', currentViewLevel, 'View scope')}

                        {/* Action-scope ladder */}
                        {showActionLadder && radioRow('action', currentActionLevel, 'Manage scope')}

                        {/* Other actions (e.g. claim, escalate, log_action) */}
                        {otherActions.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.15rem 0 0.15rem 2.4rem',
                              fontSize: '0.75rem',
                              color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span style={{ minWidth: '110px', fontWeight: 500 }}>Other</span>
                            {otherActions.map((action) => (
                              <label
                                key={action}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  cursor: visible ? 'pointer' : 'not-allowed',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={acts.includes(action)}
                                  onChange={(e) => toggleAction(m.code, action, e.target.checked)}
                                  disabled={!visible}
                                />
                                {OTHER_ACTION_LABELS[action] ?? action}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label="Status"
                options={STATUS_OPTIONS}
                value={formData.statusCode}
                onChange={(v) => setFormData((p) => ({ ...p, statusCode: (v as 'ACTIVE' | 'INACTIVE') }))}
                isClearable={false}
                isDisabled={mode === 'view' || (isSystemRole)}
              />
            </div>
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`Are you sure you want to ${mode === 'copy' ? 'create' : mode === 'create' ? 'create' : 'update'} this role?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default RoleFormModal;
